import React, { useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, UserCog } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Blocks the entire app if the logged-in user has not completed essential profile fields.
 * Required fields: full_name, phone, skill_level.
 * Allows only /auth, /auth/callback pages to render without the gate.
 */
const ProfileCompletionGate = ({ children }) => {
  const { user, API } = useContext(AuthContext);
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ full_name: '', phone: '', skill_level: '', city: '' });

  // Update form when user becomes available
  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.full_name || '',
        phone: user.phone || '',
        skill_level: user.skill_level || '',
        city: user.city || '',
      });
    }
  }, [user]);

  const isAuthPage = location.pathname.startsWith('/auth');

  const needsCompletion = !!user && !isAuthPage && (
    !user.full_name?.trim() ||
    !user.phone?.trim() ||
    !user.skill_level?.trim() ||
    !user.city?.trim()
  );

  if (!needsCompletion) return children;

  const phoneValid = /^[0-9+\-\s()]{7,}$/.test(form.phone.trim());

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.full_name.trim()) { toast.error('שם מלא חובה'); return; }
    if (!form.phone.trim()) { toast.error('מספר טלפון חובה'); return; }
    if (!phoneValid) { toast.error('מספר טלפון לא תקין'); return; }
    if (!form.skill_level) { toast.error('רמת משחק חובה'); return; }
    if (!form.city.trim()) { toast.error('עיר מגורים חובה'); return; }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.put(
        `${API}/users/profile`,
        {
          full_name: form.full_name.trim(),
          phone: form.phone.trim(),
          skill_level: form.skill_level,
          city: form.city.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('הפרופיל עודכן! מעבר לאתר 🎾');
      // Force full reload so AuthContext re-fetches the updated user
      setTimeout(() => { window.location.reload(); }, 600);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'שגיאה בעדכון פרופיל');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true}>
      <DialogContent
        dir="rtl"
        className="max-w-lg [&>button.absolute]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="profile-completion-gate"
      >
        <DialogHeader>
          <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center mb-2">
            <UserCog className="w-7 h-7 text-white" />
          </div>
          <DialogTitle className="text-center text-xl">השלמת פרופיל — חובה</DialogTitle>
          <DialogDescription className="text-center">
            ברוך הבא! לפני שתמשיך לאתר עליך למלא את הפרטים הבסיסיים.<br />
            מספר טלפון חיוני לעדכוני תחרויות והודעות חשובות.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="pc-name">שם מלא <span className="text-red-500">*</span></Label>
            <Input
              id="pc-name"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="שם פרטי ושם משפחה"
              data-testid="pc-name-input"
              required
            />
          </div>

          <div>
            <Label htmlFor="pc-email">אימייל</Label>
            <Input
              id="pc-email"
              type="email"
              value={user?.email || ''}
              readOnly
              disabled
              className="bg-slate-50 cursor-not-allowed"
              data-testid="pc-email-input"
            />
            <p className="text-xs text-slate-400 mt-1">האימייל מקושר לחשבון ולא ניתן לשינוי</p>
          </div>

          <div>
            <Label htmlFor="pc-phone">מספר טלפון <span className="text-red-500">*</span></Label>
            <Input
              id="pc-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="050-1234567"
              data-testid="pc-phone-input"
              required
            />
            {!phoneValid && form.phone.length > 0 && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> נא להזין מספר טלפון תקין
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="pc-level">רמת משחק / קטגוריה <span className="text-red-500">*</span></Label>
            <Select value={form.skill_level} onValueChange={(v) => setForm({ ...form, skill_level: v })}>
              <SelectTrigger id="pc-level" data-testid="pc-level-select">
                <SelectValue placeholder="בחר רמה" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="דרג א">דרג א (מתקדמים)</SelectItem>
                <SelectItem value="דרג ב">דרג ב (בינוני)</SelectItem>
                <SelectItem value="דרג ג">דרג ג (מתחילים)</SelectItem>
                <SelectItem value="יחידים open">יחידים open</SelectItem>
                <SelectItem value="זוגות open">זוגות open</SelectItem>
                <SelectItem value="נשים">נשים</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="pc-city">עיר מגורים <span className="text-red-500">*</span></Label>
            <Input
              id="pc-city"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              placeholder="לדוגמה: רעננה"
              data-testid="pc-city-input"
              required
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
            ⚠️ <strong>כל השדות עם * הם חובה.</strong> ללא מילוי לא תוכל להמשיך לגלוש באתר.<br />
            הפרטים מסייעים לנו לעדכן אותך בתחרויות רלוונטיות לאזורך ולרמה שלך.
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-base py-6"
            data-testid="pc-submit-btn"
          >
            {submitting ? 'שומר...' : '✓ שמור והמשך לאתר'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileCompletionGate;
